import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft } from "lucide-react";

/**
 * Print-friendly stocktake sheet. Opened from Inventory in a new tab with
 * the current filter set encoded on the URL (?dept=Milling&material=Lunasoft…).
 *
 * Columns follow the standard floor stocktake convention: department,
 * item, supplier, type, on-record qty, and a blank "counted" box for the
 * staff member's handwritten physical count. A signature line at the
 * bottom captures who did the count.
 *
 * Print behaviour is driven by @media print CSS in App.css — the header
 * bar and back button hide, and the table stays clean on A4.
 */
const StocktakePrint = () => {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const dept = params.get("dept") || "all";
  const material = params.get("material") || "all";
  const category = params.get("category") || "all";
  const supplier = params.get("supplier") || "all";
  const q = (params.get("q") || "").trim().toLowerCase();

  useEffect(() => {
    api
      .get("/stock")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load inventory"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return items
      .filter((it) => {
        if (dept !== "all") {
          const list = it.departments?.length
            ? it.departments
            : [it.department].filter(Boolean);
          if (!list.includes(dept)) return false;
        }
        if (material !== "all" && (it.material || "") !== material) return false;
        if (category !== "all" && (it.category || "") !== category) return false;
        if (supplier !== "all" && (it.supplier_default || "") !== supplier)
          return false;
        if (!q) return true;
        return (
          it.name.toLowerCase().includes(q) ||
          (it.sku || "").toLowerCase().includes(q) ||
          (it.supplier_default || "").toLowerCase().includes(q) ||
          (it.material || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // Sort by primary department then alphabetically — matches the way
        // staff actually walk the shelves (grouped by workspace).
        const da =
          (a.departments?.length ? a.departments[0] : a.department) || "";
        const db =
          (b.departments?.length ? b.departments[0] : b.department) || "";
        if (da !== db) return da.localeCompare(db);
        return a.name.localeCompare(b.name);
      });
  }, [items, dept, material, category, supplier, q]);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const activeFilters = [
    dept !== "all" && `Department: ${dept}`,
    material !== "all" && `Material: ${material}`,
    category !== "all" && `Category: ${category}`,
    supplier !== "all" && `Supplier: ${supplier}`,
    q && `Search: "${q}"`,
  ].filter(Boolean);

  // Compact "Production → Lunasoft" style label — makes the printed sheet
  // instantly identifiable when it's sitting on a bench with 5 others.
  const scopeCrumbs = [
    dept !== "all" && dept,
    material !== "all" && material,
    category !== "all" && category,
    supplier !== "all" && supplier,
  ].filter(Boolean);
  const scopeLine = scopeCrumbs.length ? scopeCrumbs.join(" → ") : "All items";

  const primaryDept = (it) =>
    (it.departments?.length ? it.departments[0] : it.department) || "—";

  return (
    <div className="stocktake-print" data-testid="stocktake-print-page">
      {/* Force portrait A4 for the stocktake sheet — the global @media
          print rule in index.css defaults to landscape for reports, but
          the stocktake fits many more items per page in portrait and
          matches the way staff hold clipboards on the shop floor. */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
        }
      `}</style>
      {/* Screen-only controls: hidden by @media print */}
      <div className="no-print sticky top-0 z-10 bg-[#FAF8F1] border-b-2 border-black px-6 py-3 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => window.close()}
          data-testid="stocktake-close-btn"
          className="nb-btn bg-white px-4 py-2 flex items-center gap-2"
        >
          <ArrowLeft size={16} strokeWidth={2.5} /> Close
        </button>
        <div className="text-sm text-[#6B6B6B]">
          {filtered.length} item{filtered.length === 1 ? "" : "s"} · use your
          browser&apos;s Print dialog to save as PDF
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="stocktake-print-btn"
          className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-2"
        >
          <Printer size={16} strokeWidth={2.5} /> Print / Save PDF
        </button>
      </div>

      <div className="p-6 max-w-[880px] mx-auto text-[#111]">
        <header className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] font-bold">
                Podlab / Footwork · Warehouse
              </div>
              <h1 className="font-display text-3xl leading-none mt-1">
                Physical stocktake sheet
              </h1>
              <div
                className="mt-2 inline-block font-bold text-lg px-3 py-1 border-2 border-black rounded-md bg-[#FFF7D6]"
                data-testid="stocktake-scope-line"
              >
                {scopeLine}
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="text-[#6B6B6B]">Date</div>
              <div className="font-bold text-base">{today}</div>
              <div className="text-[#6B6B6B] mt-2">Items</div>
              <div className="font-bold text-base">{filtered.length}</div>
            </div>
          </div>
          {activeFilters.length > 0 && (
            <div className="text-xs text-[#404040] mb-3">
              <b>Full scope:</b> {activeFilters.join(" · ")}
            </div>
          )}
          <div className="grid grid-cols-2 gap-6 text-xs">
            <div>
              <div className="text-[#6B6B6B] mb-0.5">Counted by</div>
              <div className="border-b border-black h-6"></div>
            </div>
            <div>
              <div className="text-[#6B6B6B] mb-0.5">Signature</div>
              <div className="border-b border-black h-6"></div>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="text-center text-[#6B6B6B] py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[#6B6B6B] py-12">
            No items match the selected filters.
          </div>
        ) : (
          <table
            className="w-full border-collapse text-sm"
            data-testid="stocktake-table"
          >
            <thead>
              <tr className="border-b-2 border-black">
                <th className="text-left py-2 pr-2 font-bold text-xs uppercase tracking-wider w-[100px]">
                  Dept
                </th>
                <th className="text-left py-2 pr-2 font-bold text-xs uppercase tracking-wider">
                  Item
                </th>
                <th className="text-left py-2 pr-2 font-bold text-xs uppercase tracking-wider w-[130px]">
                  Supplier
                </th>
                <th className="text-left py-2 pr-2 font-bold text-xs uppercase tracking-wider w-[70px]">
                  Type
                </th>
                <th className="text-right py-2 pr-2 font-bold text-xs uppercase tracking-wider w-[90px]">
                  On record
                </th>
                <th className="text-center py-2 font-bold text-xs uppercase tracking-wider w-[100px]">
                  Counted
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, idx) => (
                <tr
                  key={it.id}
                  className={`border-b border-[#DDD] ${
                    idx % 2 === 0 ? "" : "bg-[#F7F5EE]"
                  }`}
                  data-testid={`stocktake-row-${it.id}`}
                >
                  <td className="py-2 pr-2 align-top">{primaryDept(it)}</td>
                  <td className="py-2 pr-2 align-top">
                    <div className="font-bold">{it.name}</div>
                    {(it.material || it.colour) && (
                      <div className="text-[10px] text-[#6B6B6B]">
                        {[it.material, it.colour].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-2 align-top text-xs">
                    {it.supplier_default || "—"}
                  </td>
                  <td className="py-2 pr-2 align-top text-xs">
                    {it.type || "—"}
                  </td>
                  <td className="py-2 pr-2 align-top text-right font-mono-num tabular-nums">
                    {(it.current_qty ?? 0)} {it.unit}
                  </td>
                  <td className="py-2 align-top">
                    <div className="border-2 border-black h-8 rounded-md bg-white"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <footer className="mt-8 text-xs text-[#6B6B6B]">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="mb-1">Notes / discrepancies</div>
              <div className="border-2 border-[#CCC] rounded-md h-20 bg-white"></div>
            </div>
            <div>
              <div className="mb-1">Reviewed by (manager)</div>
              <div className="border-b border-black h-6 mb-3"></div>
              <div className="mb-1">Date</div>
              <div className="border-b border-black h-6"></div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StocktakePrint;
